"use client";

import { SizesUI } from "./SizesUI";
import { hasSizes } from "../../utils/product/hasSizes";
import { FitToolAndSizeChart } from "../FitToolAndSizeChart/FitToolAndSizeChart";
import { useAvailableSizes } from "./utils";
import {
  buttonSizeThresholdConfigSchema,
  ButtonSizeThreshold,
} from "./config/buttonSizeThresholdConfigSchema";
import { HTMLAttributes, useEffect, useMemo } from "react";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "../../constants/constants";
import { getBasket } from "@/utils/analytics/getBasket";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { Variant } from "@/types/types";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { StandardProps } from "@/components/componentMap.ts";

export const Sizes = (props: StandardProps & { sizes: string }) => {
  const { product, configProps, flags } = props;

  const isGroupingAllowed = configProps?.allowGrouping === true;
  const isSortingEnabled = configProps?.sortSizes === true;
  const withFitToolAndSizeChart = configProps?.withFitToolAndSizeChart === true;
  const showMore =
    typeof configProps?.showMore === "number"
      ? configProps?.showMore
      : undefined;

  const { brand } = product;
  const { variant } = useVariant(product);

  const showPromoOnIndicators = !!flags.showPromoOnIndicators.value;
  const useNewSizeOffers = flags.useNewSizeOffers.value === true;
  const replaceHistoryEntryOnVariantChange =
    flags.replaceHistoryEntryOnVariantChange.value === true;

  const sizesList = useAvailableSizes(
    product,
    {
      sortSizes: isSortingEnabled,
    },
    { showPromoOnIndicators },
  );

  const buttonSizeStyleThreshold: ButtonSizeThreshold | undefined =
    sizesList.some((size) => size.showReduced) && !useNewSizeOffers
      ? {
          medium: 12,
        }
      : buttonSizeThresholdConfigSchema.safeParse(
          configProps?.buttonSizeStyleThreshold,
        )?.data || undefined;

  useEffect(() => {
    const sizeAddedToBasket = () => {
      const { product: basketProducts } = getBasket();
      const sizesForProduct = basketProducts.filter(
        (item) => item.productId === product.aliases.pimProductId,
      );

      const colourProducts = product.variants.filter(
        (v: Variant) =>
          v?.differentiators?.colour === variant.differentiators?.colour &&
          sizesForProduct.some((item) => item.sku === v.aliases.skuId),
      );

      if (colourProducts.length < 2) {
        return;
      }

      if (colourProducts.length > 2) {
        dispatchMonetateQEvent("MultipleSizesOfSameColourA2B");
      } else if (colourProducts.length > 1) {
        dispatchMonetateQEvent("2SizesOfSameColourA2B");
      }
    };
    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      sizeAddedToBasket,
    );
    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        sizeAddedToBasket,
      );
    };
  }, []);

  const FitToolAndSizeChartComponent = useMemo(() => {
    return function FitToolAndSizeChartComponent(
      htmlProps: HTMLAttributes<HTMLDivElement>,
    ) {
      return (
        <div {...htmlProps}>
          <FitToolAndSizeChart {...props} />
        </div>
      );
    };
  }, [props]);

  if (!hasSizes(product)) {
    return null;
  }

  return (
    <SizesUI
      sizes={sizesList}
      isGroupingAllowed={isGroupingAllowed}
      showMore={showMore}
      buttonSizeStyleThreshold={buttonSizeStyleThreshold}
      brand={brand?.name}
      useNewSizeOffers={useNewSizeOffers}
      replaceHistoryEntryOnVariantChange={replaceHistoryEntryOnVariantChange}
      FitToolAndSizeChartComponent={FitToolAndSizeChartComponent}
      withFitToolAndSizeChart={withFitToolAndSizeChart}
    />
  );
};
